import { Tooltip as TooltipPrimitive } from 'radix-ui';
import * as React from 'react';
import { ReactNode, useCallback, useEffect, useRef } from 'react';

import { useShadowRoot } from './ShadowRootContext';
import { cn } from './utils/cn';

/**
 * Provides shared timing context for any tooltips rendered inside.
 *
 * Mount one near the app root so descendants can compose `Tooltip` without
 * passing a delay each time.
 */
function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

/**
 * Delay (ms) before allowing tooltip to open after mount in Shadow DOM.
 * Prevents false-positive hover detection when content is portaled.
 */
const SHADOW_DOM_MOUNT_DELAY_MS = 100;

/**
 * Tooltip root component with Shadow DOM workaround:
 *
 * In Shadow DOM environments (e.g. Chrome extension), Radix's pointer detection
 * can incorrectly fire `onOpenChange(true)` immediately when tooltips are rendered
 * inside portaled elements (like Dialogs). This happens because Radix uses
 * `document.elementFromPoint()` to check hover state, but this API doesn't properly
 * traverse Shadow DOM boundaries.
 *
 * This wrapper intercepts `onOpenChange` and blocks opening for a short period
 * after mount, preventing false-positive hover detection while remaining
 * imperceptible for genuine hover interactions.
 *
 * The workaround only applies when running in Shadow DOM (detected via ShadowRootContext).
 * In regular DOM, tooltips behave normally with no delay.
 *
 * @example
 * <TooltipProvider>
 *   <Tooltip>
 *     <TooltipTrigger asChild>
 *       <Button variant="ghost">
 *         <Icon name="RefreshCw" size="auto" />
 *       </Button>
 *     </TooltipTrigger>
 *     <TooltipContent>Resync with EHR</TooltipContent>
 *   </Tooltip>
 * </TooltipProvider>
 */
function Tooltip({
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const { container } = useShadowRoot();
  const isInShadowDom = Boolean(container);

  const isReadyRef = useRef(!isInShadowDom);

  useEffect(() => {
    if (!isInShadowDom) return;

    const timer = setTimeout(() => {
      isReadyRef.current = true;
    }, SHADOW_DOM_MOUNT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isInShadowDom]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && !isReadyRef.current) {
        return;
      }
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  return (
    <TooltipPrimitive.Root
      data-slot="tooltip"
      onOpenChange={onOpenChange ? handleOpenChange : undefined}
      {...props}
    />
  );
}

/**
 * Element that receives hover/focus and reveals the tooltip content.
 */
function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

/**
 * Floating panel that renders the tooltip content next to its trigger.
 *
 * Set `usesPortal={false}` to keep the content inline with the trigger's DOM
 * position, useful when the tooltip must respect a parent's stacking context
 * or transforms.
 */
function TooltipContent({
  children,
  className,
  sideOffset = 0,
  usesPortal,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  /** Render the content inside a portal. @default true */
  usesPortal?: boolean;
}) {
  // Use fullPageContainer if available so tooltips can render in
  // the same stacking context as dialogs and appear above them.
  const { container, fullPageContainer } = useShadowRoot();
  const portalTarget = fullPageContainer ?? container;

  const content = (
    <TooltipPrimitive.Content
      data-slot="tooltip-content"
      sideOffset={sideOffset}
      className={cn(
        'z-50 inline-flex w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
        className,
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-xs bg-primary fill-primary" />
    </TooltipPrimitive.Content>
  );

  if (usesPortal === false) {
    return content;
  }

  return (
    <TooltipPrimitive.Portal container={portalTarget}>
      {content}
    </TooltipPrimitive.Portal>
  );
}

// Eluve Compositions

/**
 * One-line wrapper that renders a tooltip with the given `label` on its child.
 *
 * Anything beyond a plain text label (custom side, alignment, className,
 * privacy-tagged content) means composing {@link Tooltip}, {@link TooltipTrigger},
 * and {@link TooltipContent} directly instead.
 *
 * @example
 * <SimpleTooltip label="Resync with EHR">
 *   <Button variant="ghost" onClick={resync}>
 *     <Icon name="RefreshCw" size="auto" />
 *   </Button>
 * </SimpleTooltip>
 */
function SimpleTooltip({
  children,
  label,
}: {
  children: ReactNode;
  label?: string;
}) {
  if (!label) {
    return children;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export {
  SimpleTooltip,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
};
